import { permissionGuard, eLayoutType } from '@abp/ng.core';
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { CustomLoginComponent } from './components/custom-login/custom-login.component';
import { redirectIfAuthenticatedGuard } from './guards/redirect-if-authenticated.guard';
import { coachProfileCompletionGuard } from './guards/coach-profile-completion.guard';
import { customAuthGuard } from './guards/custom-auth.guard';

const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    loadChildren: () => import('./home/home.module').then(m => m.HomeModule),
  },
  {
    path: 'auth-callback',
    loadChildren: () => import('./auth-callback/auth-callback.module').then(m => m.AuthCallbackModule),
  },
  {
    path: 'dashboard',
    loadChildren: () => import('./dashboard/dashboard.module').then(m => m.DashboardModule),
  },
  {
    path: 'account',
    loadChildren: () => import('@abp/ng.account').then(m => m.AccountModule.forLazy()),
  },
  {
    path: 'identity',
    loadChildren: () => import('@abp/ng.identity').then(m => m.IdentityModule.forLazy()),
    canActivate: [customAuthGuard],
  },
  {
    path: 'setting-management',
    loadChildren: () =>
      import('@abp/ng.setting-management').then(m => m.SettingManagementModule.forLazy()),
    canActivate: [customAuthGuard],
  },
  {
    path: 'settings/two-factor-auth',
    loadComponent: () => import('./settings/two-factor-auth/two-factor-auth.component').then(m => m.TwoFactorAuthComponent),
    canActivate: [customAuthGuard],
  },
  {
    path: 'coach-register',
    loadChildren: () => import('./coach-registration/coach-registration.module').then(m => m.CoachRegistrationModule),
    data: { layout: eLayoutType.empty }
  },
  {
    path: 'login',
    component: CustomLoginComponent,
    canActivate: [redirectIfAuthenticatedGuard],
    data: { layout: eLayoutType.empty }
  },
  {
    path: 'coach/my-students',
    loadChildren: () => import('./coach/coach-students/coach-students.module').then(m => m.CoachStudentsModule),
    canActivate: [customAuthGuard, permissionGuard, coachProfileCompletionGuard],
    data: {
      requiredPolicy: 'Io.Coaches.ManageOwnStudents'
    }
  },
  {
    path: 'coach/profile-completion',
    loadChildren: () => import('./coach/coach-profile-completion/coach-profile-completion.module').then(m => m.CoachProfileCompletionModule),
    canActivate: [customAuthGuard],
    // Not: Bu sayfa yeni kayıt olan koçlar için, permission guard kaldırıldı
    // Sayfa içinde gerekli kontroller yapılıyor
  },
  {
    path: 'admin/schools',
    loadChildren: () => import('./admin/schools/schools.module').then(m => m.SchoolsModule),
    canActivate: [customAuthGuard, permissionGuard],
    data: {
      requiredPolicy: 'AbpIdentity.Users'
    }
  },
  {
    path: 'admin/cities',
    loadChildren: () => import('./admin/cities/cities.module').then(m => m.CitiesModule),
    canActivate: [customAuthGuard, permissionGuard],
    data: {
      requiredPolicy: 'AbpIdentity.Users'
    }
  },
  {
    path: 'admin/districts',
    loadChildren: () => import('./admin/districts/districts.module').then(m => m.DistrictsModule),
    canActivate: [customAuthGuard, permissionGuard],
    data: {
      requiredPolicy: 'AbpIdentity.Users'
    }
  },
  {
    path: 'coach/dashboard',
    loadComponent: () => import('./coach/dashboard/coach-dashboard.component').then(m => m.CoachDashboardComponent),
    canActivate: [customAuthGuard, permissionGuard],
    data: {
      requiredPolicy: 'Io.FormResponses.ViewStudentResponses'
    }
  },
  {
    path: 'coach/student-report/:studentId',
    loadComponent: () => import('./coach/dashboard/student-report.component').then(m => m.StudentReportComponent),
    canActivate: [customAuthGuard, permissionGuard],
    data: {
      requiredPolicy: 'Io.FormResponses.ViewStudentResponses'
    }
  },
  {
    path: 'coach/forms',
    loadChildren: () => import('./coach/forms/forms.module').then(m => m.FormsModule),
    canActivate: [customAuthGuard, permissionGuard],
    data: {
      requiredPolicy: 'Io.Coaches.ManageOwnStudents'
    }
  },
  {
    path: 'coach/form-analytics',
    loadChildren: () => import('./coach/form-analytics/form-analytics.module').then(m => m.FormAnalyticsModule),
    canActivate: [customAuthGuard, permissionGuard],
    data: {
      requiredPolicy: 'Io.FormResponses.ViewStudentResponses'
    }
  },
  {
    path: 'coach/reports',
    loadChildren: () => import('./coach/coach-reports/coach-reports.module').then(m => m.CoachReportsModule),
    canActivate: [customAuthGuard, permissionGuard],
    data: {
      requiredPolicy: 'Io.Coaches.ManageOwnStudents'
    }
  },
  {
    path: 'coach/student-groups',
    loadChildren: () => import('./coach/student-groups/student-groups.module').then(m => m.StudentGroupsModule),
    canActivate: [customAuthGuard, permissionGuard],
    data: {
      requiredPolicy: 'Io.StudentGroups'
    }
  },
  {
    path: 'student',
    loadChildren: () => import('./student/student.module').then(m => m.StudentModule),
    canActivate: [customAuthGuard],
  },
  {
    path: 'admin/impersonation',
    loadComponent: () => import('./impersonation/components/impersonation-page/impersonation-page.component').then(m => m.ImpersonationPageComponent),
    canActivate: [customAuthGuard, permissionGuard],
    data: {
      requiredPolicy: 'Io.Users.ImpersonateUser'
    }
  },
  {
    path: 'legal',
    loadChildren: () => import('./legal-pages/legal-pages.module').then(m => m.LegalPagesModule),
    data: { layout: eLayoutType.empty }
  },
  {
    path: 'm/:token',
    loadComponent: () => import('./magic-link/magic-link.component').then(m => m.MagicLinkComponent),
    data: { layout: eLayoutType.empty }
  },
  {
    path: 'google-callback',
    loadComponent: () => import('./google-callback/google-callback.component').then(m => m.GoogleCallbackComponent),
    data: { layout: eLayoutType.empty }
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {})],
  exports: [RouterModule],
})
export class AppRoutingModule {}
